import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Drag-to-resize height for a panel docked to the bottom of its container.
 * The returned `onMouseDown` is meant for a handle sitting at the panel's
 * TOP edge — dragging it up grows the panel, dragging it down shrinks it.
 */
export function useResizableHeight(defaultHeight, { min = 120, max = 800 } = {}) {
    const [height, setHeight] = useState(defaultHeight);
    const [isResizing, setIsResizing] = useState(false);
    const startRef = useRef({ y: 0, height: defaultHeight });

    const onMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        setIsResizing(true);
        startRef.current = { y: e.clientY, height };
        e.preventDefault();
    }, [height]);

    useEffect(() => {
        if (!isResizing) return;
        const onMouseMove = (e) => {
            const delta = startRef.current.y - e.clientY; // dragging up grows the panel
            setHeight(Math.min(max, Math.max(min, startRef.current.height + delta)));
        };
        const onMouseUp = () => setIsResizing(false);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isResizing, min, max]);

    return [height, onMouseDown, isResizing];
}
